import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

import { JobService } from '../../../core/services/job.service';
import { TechnicianService } from '../../../core/services/technician.service';
import { TechnicianLocationService } from '../../../core/services/technician-location.service';
import { AuthService } from '../../../core/services/auth.service';
import { Job, JobStatus } from '../../../core/models/job.model';
import { Technician } from '../../../core/models/technician.model';

const TRACKED_STATUSES: JobStatus[] = ['EnRoute', 'Arrived', 'InProgress'];
const LOCATION_PING_INTERVAL_MS = 5 * 60 * 1000;

const STATUS_SEQUENCE: JobStatus[] = ['Created', 'Assigned', 'Scheduled', 'EnRoute', 'Arrived', 'InProgress', 'Completed'];

const PM_JOB_TYPE_NAME = 'Preventive Maintenance';

function isPreventiveMaintenanceOnly(job: Job): boolean {
  const types = job.jobTypes ?? [];
  return types.length > 0 && types.every((jt) => jt.name === PM_JOB_TYPE_NAME);
}

function describeJobActionError(err: unknown, action: 'start' | 'complete'): string {
  const httpError = err as { name?: string; error?: { error?: string } };
  if (httpError?.name === 'TimeoutError') {
    return `Request timed out - check your connection and try again.`;
  }
  return httpError?.error?.error || `Failed to ${action} job.`;
}

@Component({
  selector: 'app-my-jobs',
  imports: [CommonModule, RouterLink],
  templateUrl: './my-jobs.html',
  styleUrl: './my-jobs.scss',
})
export class MyJobs {
  private readonly jobService = inject(JobService);
  private readonly technicianService = inject(TechnicianService);
  private readonly technicianLocationService = inject(TechnicianLocationService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly authService = inject(AuthService);

  protected readonly technicians = signal<Technician[]>([]);
  protected readonly selectedTechnicianId = signal<number | null>(null);

  protected readonly matchedOwnTechnician = signal(false);
  protected readonly ownTechnicianName = signal<string | null>(null);

  protected readonly jobs = signal<Job[]>([]);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);

  private static readonly LONG_NOTES_THRESHOLD = 140;
  protected readonly expandedNotesJobIds = signal<Set<number>>(new Set());

  protected readonly nowMs = signal(Date.now());

  protected readonly startModalOpen = signal(false);
  protected readonly startModalJob = signal<Job | null>(null);
  protected readonly startNotes = signal('');
  protected readonly startPhotos = signal<string[]>([]);
  protected readonly starting = signal(false);

  protected readonly completeModalOpen = signal(false);
  protected readonly completeModalJob = signal<Job | null>(null);
  protected readonly completeNotes = signal('');
  protected readonly completePhotos = signal<string[]>([]);
  protected readonly completeServiceAdvisorEmail = signal('');
  protected readonly completeNeedsAdvisorEmail = computed(() => {
    const job = this.completeModalJob();
    return !!job && isPreventiveMaintenanceOnly(job);
  });
  protected readonly completing = signal(false);

  constructor() {
    this.technicianService.getTechnicians({ pageSize: 200 }).subscribe({
      next: (r) => {
        const active = r.items.filter((t) => t.isActive);
        this.technicians.set(active);

        const signedInEmail = (this.authService.account()?.username ?? '').toLowerCase();
        const own = signedInEmail ? active.find((t) => (t.email ?? '').toLowerCase() === signedInEmail) : undefined;
        if (own) {
          this.matchedOwnTechnician.set(true);
          this.ownTechnicianName.set(`${own.firstName ?? ''} ${own.lastName ?? ''}`.trim() || own.email);
          this.selectedTechnicianId.set(own.technicianId);
          this.loadJobs();
        }
      }
    });

    const tickIntervalId = setInterval(() => this.nowMs.set(Date.now()), 1000);
    this.destroyRef.onDestroy(() => clearInterval(tickIntervalId));

    const locationIntervalId = setInterval(() => this.reportLocationIfActive(), LOCATION_PING_INTERVAL_MS);
    this.destroyRef.onDestroy(() => clearInterval(locationIntervalId));
  }

  protected onTechnicianChange(value: string): void {
    const newId = value ? Number(value) : null;
    this.selectedTechnicianId.set(newId);

    const signedInEmail = (this.authService.account()?.username ?? '').toLowerCase();
    const selectedTech = this.technicians().find((t) => t.technicianId === newId);
    this.matchedOwnTechnician.set(!!selectedTech && (selectedTech.email ?? '').toLowerCase() === signedInEmail);

    this.loadJobs();
  }

  private loadJobs(): void {
    const technicianId = this.selectedTechnicianId();
    if (!technicianId) {
      this.jobs.set([]);
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.jobService.getJobs({ technicianId, pageSize: 100 }).subscribe({
      next: (result) => {
        this.jobs.set(result.items);
        this.loading.set(false);
        this.reportLocationIfActive();
      },
      error: (err) => {
        this.error.set('Failed to load your jobs. Is the FieldService API running?');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  private reportLocationIfActive(): void {
    if (!this.matchedOwnTechnician()) return;

    const technicianId = this.selectedTechnicianId();
    const activeJob = this.jobs().find((j) => TRACKED_STATUSES.includes(j.status));
    if (!technicianId || !activeJob || !('geolocation' in navigator)) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.technicianLocationService.updateLocation(technicianId, {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          jobId: activeJob.jobId,
        }).subscribe({ error: (err) => console.error('Failed to report location', err) });
      },
      (err) => console.error('Geolocation unavailable', err),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  }

  protected jobTypeNames(job: Job): string {
    return (job.jobTypes ?? []).map((jt) => jt.name).filter(Boolean).join(', ');
  }

  protected isLongNotes(notes: string | null): boolean {
    return !!notes && notes.length > MyJobs.LONG_NOTES_THRESHOLD;
  }

  protected isNotesExpanded(jobId: number): boolean {
    return this.expandedNotesJobIds().has(jobId);
  }

  protected toggleNotes(jobId: number): void {
    this.expandedNotesJobIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  }

  protected getDirectionsUrl(job: Job): string | null {
    if (job.dealerLatitude == null || job.dealerLongitude == null) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${job.dealerLatitude},${job.dealerLongitude}`;
  }

  private updateJobInList(updated: Job): void {
    this.jobs.update((list) => list.map((j) => (j.jobId === updated.jobId ? updated : j)));
  }

  protected nextStatus(job: Job): JobStatus | null {
    const index = STATUS_SEQUENCE.indexOf(job.status);
    if (index < 0 || index === STATUS_SEQUENCE.length - 1) return null;

    const next = STATUS_SEQUENCE[index + 1];
    return next === 'Scheduled' && isPreventiveMaintenanceOnly(job) ? 'EnRoute' : next;
  }

  protected canAdvance(job: Job): boolean {
    const next = this.nextStatus(job);
    return next !== null && next !== 'Scheduled' && job.status !== 'Arrived' && job.status !== 'InProgress';
  }

  protected canCancel(job: Job): boolean {
    return job.status !== 'Completed' && job.status !== 'Cancelled';
  }

  protected advanceJob(job: Job): void {
    const next = this.nextStatus(job);
    if (!next) return;
    this.jobService.updateJobStatus(job.jobId, next).subscribe({
      next: (updated) => this.updateJobInList(updated),
      error: (err) => {
        this.error.set(err.error?.error || 'Failed to update job status.');
        console.error(err);
      }
    });
  }

  protected cancelJob(job: Job): void {
    this.jobService.updateJobStatus(job.jobId, 'Cancelled').subscribe({
      next: (updated) => this.updateJobInList(updated),
      error: (err) => {
        this.error.set(err.error?.error || 'Failed to cancel job.');
        console.error(err);
      }
    });
  }

  protected resumeJob(job: Job): void {
    this.jobService.resumeJob(job.jobId).subscribe({
      next: (updated) => this.updateJobInList(updated),
      error: (err) => {
        this.error.set(err.error?.error || 'Failed to resume job.');
        console.error(err);
      }
    });
  }

  protected isRunning(job: Job): boolean {
    return job.status === 'InProgress' && !!job.workStartedAt;
  }

  protected isPaused(job: Job): boolean {
    return job.status === 'InProgress' && !job.workStartedAt;
  }

  protected isCompleted(job: Job): boolean {
    return job.status === 'Completed';
  }

  protected hasTimeTracking(job: Job): boolean {
    return this.isRunning(job) || this.isPaused(job) || this.isCompleted(job);
  }

  protected elapsedSeconds(job: Job): number {
    const base = job.accumulatedSeconds ?? 0;
    if (job.status === 'InProgress' && job.workStartedAt) {
      const startedMs = new Date(job.workStartedAt).getTime();
      return base + Math.max(0, Math.floor((this.nowMs() - startedMs) / 1000));
    }
    return base;
  }

  protected formatDuration(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
  }

  protected actualTimeColor(job: Job): string {
    if (!job.durationMinutes) return '#00B9F2';
    return this.elapsedSeconds(job) <= job.durationMinutes * 60 ? '#8DC63F' : '#F29727';
  }

  private readPhotos(event: Event): Promise<string[]> {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) {
      return Promise.resolve([]);
    }

    const readers = Array.from(files).map((file) => new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }));

    input.value = '';
    return Promise.all(readers);
  }

  protected openStartModal(job: Job): void {
    this.startModalJob.set(job);
    this.startNotes.set('');
    this.startPhotos.set([]);
    this.startModalOpen.set(true);
  }

  protected closeStartModal(): void {
    this.startModalOpen.set(false);
    this.startModalJob.set(null);
  }

  protected onStartPhotosSelected(event: Event): void {
    this.readPhotos(event).then((dataUrls) => this.startPhotos.update((list) => [...list, ...dataUrls]));
  }

  protected removeStartPhoto(index: number): void {
    this.startPhotos.update((list) => list.filter((_, i) => i !== index));
  }

  protected confirmStartJob(): void {
    const job = this.startModalJob();
    if (!job) return;

    this.starting.set(true);
    this.jobService.startJob(job.jobId, { notes: this.startNotes() || undefined, photosBase64: this.startPhotos() }).subscribe({
      next: (updated) => {
        this.updateJobInList(updated);
        this.starting.set(false);
        this.closeStartModal();
      },
      error: (err) => {
        this.starting.set(false);
        this.error.set(describeJobActionError(err, 'start'));
        console.error(err);
      }
    });
  }

  protected openCompleteModal(job: Job): void {
    this.completeModalJob.set(job);
    this.completeNotes.set('');
    this.completePhotos.set([]);
    this.completeServiceAdvisorEmail.set(job.dealerServiceAdvisorEmail ?? job.dealerEmail ?? '');
    this.completeModalOpen.set(true);
  }

  protected closeCompleteModal(): void {
    this.completeModalOpen.set(false);
    this.completeModalJob.set(null);
  }

  protected onCompletePhotosSelected(event: Event): void {
    this.readPhotos(event).then((dataUrls) => this.completePhotos.update((list) => [...list, ...dataUrls]));
  }

  protected removeCompletePhoto(index: number): void {
    this.completePhotos.update((list) => list.filter((_, i) => i !== index));
  }

  protected confirmCompleteJob(): void {
    const job = this.completeModalJob();
    if (!job) return;

    this.completing.set(true);
    this.jobService.completeJob(job.jobId, {
      notes: this.completeNotes() || undefined,
      photosBase64: this.completePhotos(),
      serviceAdvisorEmail: this.completeNeedsAdvisorEmail() ? (this.completeServiceAdvisorEmail() || undefined) : undefined,
    }).subscribe({
      next: (updated) => {
        this.updateJobInList(updated);
        this.completing.set(false);
        this.closeCompleteModal();
      },
      error: (err) => {
        this.completing.set(false);
        this.error.set(describeJobActionError(err, 'complete'));
        console.error(err);
      }
    });
  }

  protected statusColor(status: JobStatus): string {
    switch (status) {
      case 'Created': return '#64748B';
      case 'Assigned': return '#00B9F2';
      case 'Scheduled': return '#14b8a6';
      case 'EnRoute': return '#F29727';
      case 'Arrived': return '#a855f7';
      case 'InProgress': return '#eab308';
      case 'Completed': return '#8DC63F';
      case 'Cancelled': return '#ef4444';
      default: return '#64748B';
    }
  }
}
